import { type CSSProperties, useState, useMemo, useRef, useEffect } from 'react';
import type { CountryOption } from './country-utils';

export interface CountryPickerProps {
  options: CountryOption[];
  valueIso2: string;
  onChangeIso2: (iso2: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

const triggerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  height: 'var(--input-height, 42px)',
  padding: '0 var(--input-padding-x, 16px)',
  borderRadius: 'var(--input-radius, 10px)',
  border: '1px solid var(--border, #e5e7eb)',
  background: 'var(--input-bg, #ffffff)',
  fontSize: 'var(--font-size-base, 14px)',
  color: 'var(--input-text, #0a0a0a)',
  fontFamily: 'inherit',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  minWidth: 0,
  boxSizing: 'border-box',
};

const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: 4,
  background: 'var(--bg-surface, #ffffff)',
  border: '1px solid var(--border, #e5e7eb)',
  borderRadius: 'var(--input-radius, 10px)',
  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  zIndex: 50,
  maxHeight: 240,
  minWidth: '100%',
  width: 'max-content',
  maxWidth: 320,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
};

const searchInputStyle: CSSProperties = {
  width: '100%',
  padding: '8px var(--input-padding-x, 16px)',
  border: 'none',
  borderBottom: '1px solid var(--border, #e5e7eb)',
  fontSize: 'var(--font-size-base, 14px)',
  fontFamily: 'inherit',
  outline: 'none',
  background: 'transparent',
  boxSizing: 'border-box',
};

const optionStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: 36,
  padding: '8px var(--input-padding-x, 16px)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-base, 14px)',
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export function CountryPicker({
  options,
  valueIso2,
  onChangeIso2,
  placeholder = 'Select country...',
  disabled,
}: CountryPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => options.find((o) => o.iso2 === valueIso2), [options, valueIso2]);

  const filtered = useMemo(() => {
    if (!search.trim()) return options;
    const q = search.toLowerCase().trim();
    return options.filter((o) => o.searchText.includes(q));
  }, [options, search]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => {
          if (!disabled) setOpen(!open);
        }}
        style={{
          ...triggerStyle,
          opacity: disabled ? 0.6 : 1,
          cursor: disabled ? 'default' : 'pointer',
        }}
      >
        {selected ? selected.label : placeholder}
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{ marginLeft: 'auto', flexShrink: 0 }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div style={dropdownStyle}>
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search country..."
            style={searchInputStyle}
          />
          {filtered.length === 0 && (
            <div
              style={{
                padding: '12px var(--input-padding-x, 16px)',
                color: 'var(--text-muted, #6b7280)',
                fontSize: 'var(--font-size-sm, 12px)',
              }}
            >
              No matches
            </div>
          )}
          {filtered.map((opt) => (
            <div
              key={opt.iso2}
              onClick={() => {
                onChangeIso2(opt.iso2);
                setOpen(false);
                setSearch('');
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'var(--bg-surface-2, #f9fafb)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'transparent';
              }}
              style={{
                ...optionStyle,
                background: opt.iso2 === valueIso2 ? 'var(--bg-surface-2, #f9fafb)' : 'transparent',
                fontWeight: opt.iso2 === valueIso2 ? 600 : 400,
              }}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
